import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  WifiOff,
  ServerCrash,
  Lock,
  Clock,
  AlertTriangle,
  RefreshCw,
  Home,
  Mail,
  LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

export type ErrorType =
  | 'network'
  | 'server'
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'timeout'
  | 'validation'
  | 'unknown';

interface ApiErrorStateProps {
  type?: ErrorType;
  title?: string;
  description?: string;
  icon?: LucideIcon;
  error?: Error | null;
  statusCode?: number;
  onRetry?: () => void;
  retryLabel?: string;
  showHomeButton?: boolean;
  showContactButton?: boolean;
  compact?: boolean;
  className?: string;
  children?: ReactNode;
}

const errorConfig: Record<ErrorType, { icon: LucideIcon; title: string; description: string }> = {
  network: {
    icon: WifiOff,
    title: 'Sem ligação à internet',
    description: 'Verifique a sua ligação e tente novamente.',
  },
  server: {
    icon: ServerCrash,
    title: 'Erro do servidor',
    description: 'O servidor encontrou um problema. Por favor, tente mais tarde.',
  },
  unauthorized: {
    icon: Lock,
    title: 'Sessão expirada',
    description: 'A sua sessão expirou. Por favor, inicie sessão novamente.',
  },
  forbidden: {
    icon: Lock,
    title: 'Acesso negado',
    description: 'Não tem permissão para aceder a este recurso.',
  },
  'not-found': {
    icon: AlertCircle,
    title: 'Não encontrado',
    description: 'O recurso solicitado não foi encontrado.',
  },
  timeout: {
    icon: Clock,
    title: 'Tempo esgotado',
    description: 'O pedido demorou demasiado tempo. Tente novamente.',
  },
  validation: {
    icon: AlertTriangle,
    title: 'Dados inválidos',
    description: 'Os dados fornecidos contêm erros. Verifique e tente novamente.',
  },
  unknown: {
    icon: AlertCircle,
    title: 'Ocorreu um erro',
    description: 'Algo correu mal. Por favor, tente novamente.',
  },
};

// Helper to determine error type from status code
export const getErrorTypeFromStatus = (status: number): ErrorType => {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not-found';
  if (status === 408 || status === 504) return 'timeout';
  if (status === 422 || status === 400) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
};

// Helper to determine error type from error object
export const getErrorTypeFromError = (error: Error): ErrorType => {
  const message = error.message.toLowerCase();

  if (message.includes('network') || message.includes('fetch')) return 'network';
  if (message.includes('timeout')) return 'timeout';
  if (message.includes('unauthorized') || message.includes('401')) return 'unauthorized';
  if (message.includes('forbidden') || message.includes('403')) return 'forbidden';
  if (message.includes('not found') || message.includes('404')) return 'not-found';

  return 'unknown';
};

const ApiErrorState = ({
  type,
  title,
  description,
  icon: CustomIcon,
  error,
  statusCode,
  onRetry,
  retryLabel = 'Tentar novamente',
  showHomeButton = true,
  showContactButton = false,
  compact = false,
  className,
  children,
}: ApiErrorStateProps) => {
  // Determine error type from props
  let errorType: ErrorType = type || 'unknown';
  if (!type) {
    if (statusCode) {
      errorType = getErrorTypeFromStatus(statusCode);
    } else if (error) {
      errorType = getErrorTypeFromError(error);
    }
  }

  const config = errorConfig[errorType];
  const Icon = CustomIcon || config.icon;
  const displayTitle = title || config.title;
  const displayDescription = description || config.description;

  // Compact version for inline use
  if (compact) {
    return (
      <Alert variant="destructive" className={cn("my-4", className)}>
        <Icon className="h-4 w-4" />
        <AlertTitle>{displayTitle}</AlertTitle>
        <AlertDescription className="flex flex-col gap-2">
          <span>{displayDescription}</span>
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry} className="w-fit">
              <RefreshCw className="h-3 w-3 mr-2" />
              {retryLabel}
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  // Full version
  return (
    <div className={cn("flex flex-col items-center justify-center py-12 px-4 text-center", className)}>
      <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-6">
        <Icon className="h-8 w-8 text-destructive" />
      </div>
      <h3 className="text-xl font-semibold mb-2">{displayTitle}</h3>
      <p className="text-muted-foreground max-w-md mb-6">{displayDescription}</p>

      {/* Debug info in development */}
      {import.meta.env.DEV && error && (
        <div className="bg-muted rounded-lg p-4 text-sm text-left max-w-md w-full mb-6">
          <p className="font-mono text-xs text-destructive break-all">{error.message}</p>
          {statusCode && (
            <p className="font-mono text-xs text-muted-foreground mt-1">Status: {statusCode}</p>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        {onRetry && (
          <Button onClick={onRetry}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {retryLabel}
          </Button>
        )}
        {showHomeButton && (
          <Button variant="outline" asChild>
            <Link to="/">
              <Home className="h-4 w-4 mr-2" />
              Voltar ao início
            </Link>
          </Button>
        )}
        {showContactButton && (
          <Button variant="ghost" asChild>
            <Link to="/contactos">
              <Mail className="h-4 w-4 mr-2" />
              Contactar suporte
            </Link>
          </Button>
        )}
      </div>

      {children}
    </div>
  );
};

export default ApiErrorState;
